/* eslint-disable import/no-cycle */
import { postData } from '../common/api';
import { createEmbeddedPageRequest } from '../common/apiRequest';
import { getOfferEndpoint } from '../common/constants';
import { logger, logtail } from '../common/logger';
import { mutexManager, StaticMutexKeys } from '../common/mutexmanager';
import { getPage } from '../common/pageData';
import { logRuntimeError, logtailMiddleware } from '../common/utils';
import {
  onAutoFreeGiftEligibleResponse,
  requestForPdpFgUsingStoredData,
} from '../freegift';
import {
  isOfferValidForCurrentPage,
  filterWidgets,
  isFreegiftWithCodeEligible,
  onFreegiftNotEligibleResponse,
  onModuleOfferEligible,
  setFreeGiftDiscountCodesMapping,
} from './commonModuleForEmbeddedAndAutoAdd';
import { CartPageDiscountConfigType, WidgetType } from './commonTypes';

type InitConfigType = {
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled;
  widgets: WidgetType[];
};

type InitTypes = {
  config: InitConfigType;
  productFormSelector: string;
};

export function checkAutoAddOffer({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  productFormSelector,
}: {
  widgets: WidgetType[];
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
  productFormSelector: string;
}) {
  const filteredWidgets = filterWidgets({
    offerTypes: ['freegift'],
    displayType: 'embedded',
    widgets,
    availingType: 'auto_add',
  });
  for (let i = 0; i < filteredWidgets.length; i += 1) {
    if (isOfferValidForCurrentPage({ widget: filteredWidgets[i] })) {
      checkFGAutoAddOffersAndAddFG({
        widget: filteredWidgets[i],
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        productFormSelector,
      });
    }
  }
  if (getPage() === 'cart') {
    requestForPdpFgUsingStoredData({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      productFormSelector,
    });
  }
}

export async function checkFGAutoAddOffersAndAddFG({
  widget,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  productFormSelector,
  apiCart,
  isCalledFromCartDrawer = false,
  customVariantsWithQuantity = [],
}: {
  widget: WidgetType;
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
  productFormSelector?: string;
  apiCart?: any;
  isCalledFromCartDrawer?: boolean;
  customVariantsWithQuantity?: any[];
}) {
  const { id, availingType, offerType, triggerType } = widget;

  const widgetMutex = mutexManager.getMutex(id.toString());

  const fgMutex = mutexManager.getMutex(StaticMutexKeys.FREEGIFT);
  try {
    await fgMutex.acquire();
    // ensure that a widget is being evaluated only once at a time
    if (!widgetMutex.isFree()) {
      logger.log('duplicate');
      return;
    }
    await widgetMutex.acquire();

    // create the request
    const req = await createEmbeddedPageRequest({
      source: triggerType,
      offerType,
      apiCart,
      widget,
      productFormSelector,
      isCalledFromCartDrawer,
      customVariantsWithQuantity,
    });

    // send the request
    const response = await postData(getOfferEndpoint(), req);

    if (!response) {
      return;
    }

    let { is_eligible } = response;
    const { offer } = response;

    logtailMiddleware({
      widgetId: widget.id,
      basicOfferData: {
        isEligible: is_eligible,
        offered: offer.offered,
        mrOfferId: response.offer.mr_offer_id,
      },
      offerName: 'autoAddFreeGift',
    });

    await setFreeGiftDiscountCodesMapping(response, widget);

    // update eligibility status if offer is freegift with discount code
    const {
      is_eligible: isFgWithCodeEligible,
      isFreegift,
    } = isFreegiftWithCodeEligible({
      offerType,
      is_eligible,
      offer,
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
    });

    if (isFreegift) {
      is_eligible = isFgWithCodeEligible;
    }

    // functions to be called when offer is eligible
    if (is_eligible) {
      await onModuleOfferEligible({ offer });

      const isFreeGiftAdded = await onAutoFreeGiftEligibleResponse({
        offer,
        mrCart: req.cart,
        widget,
      });

      if (isFreeGiftAdded) {
        const cartDiscountModule = await import('../cartDiscount');
        cartDiscountModule.checkAndUpdateCartDiscount({
          cartPageDiscountConfig,
          isDiscountCodeEnabled,
          apiCart: null, // setting to null as the cart is old
          // passing discountCode as undefined as it is optional and avoiding unnecessary check
          discountCode: undefined,
        });
      }

      // for free git auto add there is no widget to display
      if (offerType === 'freegift' && availingType === 'auto_add') {
        return;
      }
    }

    const isPdpFgOfferOnProductPage =
      getPage() === 'product' &&
      offerType === 'freegift' &&
      triggerType === 'product_page' &&
      !isCalledFromCartDrawer;

    // functions to run if offer is not eligible
    // we can't auto remove if pdp fg isn't eligible in a product page. Auto remove can only happen in cart.
    // for ex, if offer is set to be eligible on a particular collection, it won't be eligible when customer visits a pdp of a product that is outside of the collection.
    // in that case, we can't run onFreegiftNotEligibleResponse function because it will lead to auto removing the freegift which is, wrong.
    if (!is_eligible && !isPdpFgOfferOnProductPage) {
      await onFreegiftNotEligibleResponse({
        offerType,
        availingType,
        mrCart: req.cart,
        widget,
      });
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'autoAddFreegift',
        level: 'error',
        functionName: 'checkFGAutoAddOffersAndAddFG',
      },
      errorStack: e,
    });
  } finally {
    widgetMutex.release();
    fgMutex.release();
  }
}

export async function checkFGAutoAddCartAndCheckoutPageOffer({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  apiCart,
}) {
  for (let i = 0; i < widgets.length; i += 1) {
    const { offerType, displayType, triggerType } = widgets[i];
    if (
      displayType === 'embedded' &&
      ['cart_page', 'checkout_page'].includes(triggerType)
    ) {
      if (['crossell', 'freegift'].includes(offerType)) {
        checkFGAutoAddOffersAndAddFG({
          widget: widgets[i],
          cartPageDiscountConfig,
          isDiscountCodeEnabled,
          apiCart,
        });
      }
    }
  }
}

function onPostApplyOrRemoveFDC({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  widgets,
}: {
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
  widgets: WidgetType[];
}) {
  for (let i = 0; i < widgets.length; i += 1) {
    const { offerType, displayType, triggerType, availingType } = widgets[i];
    if (displayType === 'embedded' && triggerType === 'cart_page') {
      if (['freegift'].includes(offerType)) {
        if (availingType === 'auto_add') {
          checkFGAutoAddOffersAndAddFG({
            widget: widgets[i],
            cartPageDiscountConfig,
            isDiscountCodeEnabled,
            isCalledFromCartDrawer: true,
            productFormSelector: '',
          });
        }
      }
    }
  }
}

export function registerPostApplyOrRemoveFDCEvent({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  widgets,
}: {
  cartPageDiscountConfig: CartPageDiscountConfigType;
  isDiscountCodeEnabled: boolean;
  widgets: WidgetType[];
}) {
  document.addEventListener('mrDiscountCode:apply', () => {
    logger.log('mrDiscountCode:apply custom event called');
    onPostApplyOrRemoveFDC({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      widgets,
    });
  });

  document.addEventListener('mrDiscountCode:remove', () => {
    logger.log('mrDiscountCode:remove custom event called');
    onPostApplyOrRemoveFDC({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      widgets,
    });
  });
}

export function init({ config, productFormSelector }: InitTypes) {
  const { cartPageDiscountConfig, isDiscountCodeEnabled, widgets } = config;
  checkAutoAddOffer({
    widgets,
    cartPageDiscountConfig,
    isDiscountCodeEnabled,
    productFormSelector,
  });
  registerPostApplyOrRemoveFDCEvent({
    isDiscountCodeEnabled,
    cartPageDiscountConfig,
    widgets,
  });
}
